import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { siteLabel } from '../../lib/sites';
import { formatSourceDate } from '../../lib/formatting';
import { calendarEventUrl, followUpMailto } from '../../lib/action-links';
import { verbLine, snoozeLine } from '../../lib/conflict-copy';
import { useTeamSettings, useCurrentUserId } from '../../hooks/useTeamSettings';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import type { BriefTask, BriefTaskConfidenceBand } from '../../hooks/useTeamTodaySurface';
import { recordConflictOutcome, isSessionRecordedVerb } from '../../lib/conflict-local';

/*
 * ConflictResolveModal — the honest-verb resolution surface for a surfaced decision conflict
 * (webapp-actions rung 1, §6.6). Replaces the blunt bug-423 "dismiss" with the real verb set.
 *
 * On open it POSTs `/conflicts/[edgeKey]/card` to fetch the TWO real decisions (BriefTask carries no
 * decision strings — the group task is just a title/detail). The two raw `options` are ALWAYS present;
 * the editorial layer (headline/standfirst/consequence/per-side judgment) is attached only when the
 * gpt-5.5 compose succeeded (`composed`). So `composed===false` renders the raw two-decision view —
 * never blank, never a hallucinated card (route's dead-end note).
 *
 * Band gate (§6.6 / R1 — the spec's top risk): low/uncertain conflicts get the lightweight "is this
 * even a conflict?" eval-only prompt; high/medium get the full "pick a side" modal. Asking two people
 * to adjudicate a disagreement the detector isn't sure exists is the failure this rung must avoid.
 * "It is a conflict" in eval mode escalates the SAME modal to full mode (the human affirming the
 * conflict makes pick-a-side R1-safe) — no separate positive-label write exists on the resolve route.
 */

export interface ConflictCardOption {
  decisionId: string;
  decisionText: string;
  source?: string | null;
  author?: string | null;
  judgment?: string | null;
  sourceConversationId?: string | null; // the conversation this decision came from (traceability click-through)
  decidedAt?: string | null; // when it was decided — ISO; rendered as a short date in the attribution line
}

// Short absolute date for the attribution line ("Jul 6"); null on an unparseable value so it drops out.
// Was a local copy that dropped BOTH the year and the UTC pin: decisions.decided_at already spans
// Apr-Jul and will span years, so a bare "Feb 6" on a 2023 decision reads as this February; and without
// timeZone:'UTC' the same decision renders a day earlier west of Greenwich. The shared formatter fixes
// both and keeps this date identical to the one the brief cards show for the same material.
const shortDate = formatSourceDate;

/** An outstanding ping/blocked/route on this conflict — the server's "sent — awaiting {name}" receipt. */
export interface ConflictCardAwaiting {
  kind: 'ping' | 'blocked' | 'route';
  targetName?: string | null;
  targetUserId?: string;
  byUserId?: string;
  since?: string | null;
}

export interface ConflictCardResponse {
  edgeKey: string;
  topic?: string | null;
  composed: boolean;
  headline?: string | null;
  standfirst?: string | null;
  consequence?: string | null;
  options: ConflictCardOption[];
  // All three are optional: a SPA deployed ahead of the BFF simply falls back to its local copy
  // mirror and renders no awaiting pill, rather than breaking the card.
  awaiting?: ConflictCardAwaiting | null;
  verbCopy?: Partial<Record<string, string>> | null;
  snoozeCopy?: Partial<Record<string, string>> | null;
}

type ResolveChoice = 'decision' | 'custom' | 'not_conflict' | 'snooze' | 'blocked' | 'ping' | 'route';

export interface ResolveBody {
  choice: ResolveChoice;
  decisionId?: string;
  resolutionText?: string;
  blockedOnUserId?: string;
  pingUserId?: string;
  routeUserId?: string;
  note?: string;
  // Real snooze modes (migration 140 rail): `timer` needs snoozeUntil (ISO); `until_inflow`
  // has no clock — the row's existence suppresses until a new edge lands on the pair.
  snoozeMode?: 'timer' | 'until_inflow';
  snoozeUntil?: string;
}

export interface GroupResolvePlan {
  primary: { itemId: string; body: ResolveBody };
  siblings: Array<{ itemId: string; body: ResolveBody }>;
}

/**
 * The body to fan out to each SIBLING member edge of a grouped conflict card. A grouped card is a
 * connected component of contradicting decisions (MVP groupConflictItems); the modal only pick-a-sides
 * the REPRESENTATIVE pair, but resolving the card must settle every member or the un-flipped siblings
 * re-form the identical card on the next brief (spec O1 — the "group-dismiss fan-out" the contract's
 * memberBriefingItemIds exists for). Terminal verbs (decision/custom/not_conflict) fan out; snooze and
 * blocked return null — they leave the edge `open` by design (snooze defers, blocked routes a person),
 * so an unresolved conflict correctly returns next brief.
 *
 * A sibling pair is a DIFFERENT two decisions than the representative, so a picked winner id is invalid
 * there — record the group's chosen answer as that pair's canonical TEXT instead ("the team went with X").
 */
function siblingBodyFor(
  body: ResolveBody,
  options: Array<Pick<ConflictCardOption, 'decisionId' | 'decisionText'>>,
): ResolveBody | null {
  if (body.choice === 'not_conflict') return { choice: 'not_conflict' };
  if (body.choice === 'custom') return { choice: 'custom', resolutionText: body.resolutionText };
  if (body.choice === 'decision') {
    const winnerText = options.find((o) => o.decisionId === body.decisionId)?.decisionText;
    return { choice: 'custom', resolutionText: winnerText || 'Resolved with the chosen decision' };
  }
  return null; // snooze / blocked / ping / route → no fan-out (all leave the edge open by design)
}

/**
 * Plan the full set of resolve POSTs for one user action: the representative first (§ "highest-confidence
 * first"), then one normalized call per OTHER member edge. Pure + exported so the fan-out logic is unit
 * tested without React Query. The representative is excluded from siblings (memberBriefingItemIds includes
 * it), and empty/absent member lists degrade to representative-only (= today's single-pair behavior).
 */
export function planGroupResolve(
  edgeKey: string,
  body: ResolveBody,
  memberBriefingItemIds: string[] | null | undefined,
  options: Array<Pick<ConflictCardOption, 'decisionId' | 'decisionText'>>,
): GroupResolvePlan {
  const primary = { itemId: edgeKey, body };
  const sibling = siblingBodyFor(body, options);
  if (!sibling) return { primary, siblings: [] };
  const siblingIds = (memberBriefingItemIds ?? []).filter((id) => Boolean(id) && id !== edgeKey);
  return { primary, siblings: siblingIds.map((itemId) => ({ itemId, body: sibling })) };
}

/**
 * The confidence-band gate (§6.6 / R1): low/uncertain → the eval-only prompt; high/medium → the full
 * resolution modal. An absent band errs toward the full modal — a conflict task always carries a band
 * from the backend now, and the richer surface is the safe default for the (structurally-impossible)
 * legacy case.
 */
export function isEvalOnlyBand(band: BriefTaskConfidenceBand | null | undefined): boolean {
  return band === 'low' || band === 'uncertain';
}

function OptionCard({
  option,
  selectable,
  selected,
  onSelect,
  onOpenSource,
}: {
  option: ConflictCardOption;
  selectable: boolean;
  selected: boolean;
  onSelect: () => void;
  onOpenSource?: (conversationId: string) => void; // traceability: open the source conversation
}) {
  // Attribution reads WHO · WHERE · WHEN (person first — the source name is a chip, not the headline).
  const attribution =
    [option.author, option.source ? siteLabel(option.source) : null, shortDate(option.decidedAt)]
      .filter(Boolean)
      .join(' · ') || 'Source';
  const convId = option.sourceConversationId;
  return (
    <div
      className={`rounded-2xl border p-4 text-left transition ${
        selected
          ? 'border-acc bg-acc-soft'
          : 'border-[#e1d7c8] bg-[#fffaf3] dark:border-border dark:bg-card'
      }${selectable ? ' cursor-pointer hover:-translate-y-0.5 hover:border-acc hover:shadow-[0_10px_18px_rgba(88,69,42,.08)]' : ''}`}
      onClick={selectable ? onSelect : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              // Enter/Space pick this side — the card is a role="button" but was not keyboard-operable.
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      aria-pressed={selectable ? selected : undefined}
    >
      <div className="text-[15px] font-semibold leading-5">{option.decisionText}</div>
      {/* Body prose, not a label — text-prose-foreground (bug-615): muted measures 3.55:1 on this
        * #fffaf3 option card in the cream default theme, under the 4.5:1 AA floor. */}
      {option.judgment && <div className="mt-1 text-sm leading-5 text-prose-foreground">{option.judgment}</div>}
      {convId && onOpenSource ? (
        // stopPropagation so opening the source never doubles as picking this side.
        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1 border-b border-transparent pb-0.5 font-['DM_Mono'] text-[10px] uppercase tracking-[.08em] text-muted-foreground transition hover:border-current hover:text-acc"
          onClick={(e) => {
            e.stopPropagation();
            onOpenSource(convId);
          }}
        >
          {attribution} <span aria-hidden="true">↗</span>
        </button>
      ) : (
        <div className="mt-3 font-['DM_Mono'] text-[10px] uppercase tracking-[.08em] text-muted-foreground">{attribution}</div>
      )}
    </div>
  );
}

export function ConflictResolveModal({
  task,
  onResolved,
  onClose,
}: {
  task: BriefTask;
  onResolved: (task: BriefTask) => void; // successful verb → local-hide + close (owner: StandardBrief)
  onClose: () => void; // cancel / backdrop → just close
  // No onNav: this dialog must never navigate the app. Its source link opens a new tab instead —
  // see onOpenSource below.
}) {
  useEscapeToClose(onClose);
  const edgeKey = task.briefingItemId ?? '';
  const queryClient = useQueryClient();
  const team = useTeamSettings();
  const currentUserId = useCurrentUserId();

  // low/uncertain start in the eval-only prompt; "It is a conflict" escalates to full.
  const [mode, setMode] = useState<'eval' | 'full'>(isEvalOnlyBand(task.band) ? 'eval' : 'full');
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);
  const [customText, setCustomText] = useState('');
  // One teammate picker feeds BOTH Route ("I'm blocked on them") and Ping ("here's a note"); pingNote is
  // the user-authored message a Ping carries.
  const [teammateId, setTeammateId] = useState('');
  const [pingNote, setPingNote] = useState('');

  const card = useQuery({
    queryKey: ['conflict-card', edgeKey],
    queryFn: () =>
      apiFetch<ConflictCardResponse>(`/api/surfaces/team/conflicts/${edgeKey}/card`, { method: 'POST' }),
    enabled: Boolean(edgeKey),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });

  const postResolve = (itemId: string, body: ResolveBody) =>
    apiFetch<{ ok: boolean }>(`/api/surfaces/team/conflicts/${itemId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

  const resolve = useMutation({
    mutationFn: async (body: ResolveBody) => {
      const plan = planGroupResolve(edgeKey, body, task.memberBriefingItemIds, options);
      // Representative first: if THIS fails the whole action fails (nothing changed). Its escalation
      // retract + eval label are the ones the user directly acted on.
      await postResolve(plan.primary.itemId, plan.primary.body);
      if (plan.siblings.length > 0) {
        // Best-effort per sibling — the representative already committed, so a sibling failure is logged
        // and left to the daily regen / escalate-pass backstop rather than rolling back the user's resolution.
        await Promise.all(
          plan.siblings.map((s) =>
            postResolve(s.itemId, s.body).catch((err) =>
              console.warn('[conflicts/resolve] group fan-out failed for member', s.itemId, err),
            ),
          ),
        );
      }
    },
    onSuccess: (_data, body) => {
      // The stored brief row won't drop this task until it regenerates, so record the outcome
      // in the session-local store (badge + Actions + brief all consult it) and hide locally.
      // Narrow, never cast: ping and route are NOT session outcomes. `outcomeClears` treats anything
      // but `blocked` as cleared, so recording a route here would drop a still-open conflict out of the
      // brief, the nav badge and the Actions lane — with no receipt anywhere.
      if (isSessionRecordedVerb(body.choice)) {
        const how =
          body.choice === 'decision'
            ? options.find((o) => o.decisionId === body.decisionId)?.decisionText
            : body.choice === 'custom'
              ? body.resolutionText
              : undefined;
        recordConflictOutcome(edgeKey, {
          verb: body.choice,
          how,
          wake: body.choice === 'snooze' ? (body.snoozeMode === 'timer' ? 'wakes tomorrow 9a' : 'until it moves again') : undefined,
          title: (composed && card.data?.headline) || card.data?.topic || task.title,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-today'] });
      // The durable record (brief Decision ledger + Actions "The record"): applyResolution writes
      // decision_resolutions BEFORE the route responds, so an immediate refetch sees the settle.
      queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-resolutions'] });
      onResolved(task);
    },
    onError: (err) => console.warn('[conflicts/resolve] failed; nothing was changed', err),
  });

  const composed = card.data?.composed ?? false;
  const options = card.data?.options ?? [];
  // Only offer the Blocked-on picker once we know who "me" is, so the current user can never appear
  // in their own list (self-routing a conflict is nonsensical). Empty until useCurrentUserId resolves.
  const me = currentUserId.data;
  const teammates = me ? (team.data?.members ?? []).filter((m) => m.user_id !== me) : [];

  const canResolve = customText.trim().length > 0 || Boolean(selectedDecisionId);
  const submitResolve = () => {
    // A typed answer is a first-class outcome and takes precedence over a picked side (§6.6).
    if (customText.trim().length > 0) resolve.mutate({ choice: 'custom', resolutionText: customText.trim() });
    else if (selectedDecisionId) resolve.mutate({ choice: 'decision', decisionId: selectedDecisionId });
  };

  const busy = resolve.isPending;
  const badge = mode === 'eval' ? 'Is this a conflict?' : 'Resolution workspace';
  const title = (composed && card.data?.headline) || card.data?.topic || task.title;

  // Redesign register (approved prototype CardDialog): cream sheet, DM Mono eyebrow, Instrument
  // Serif title, path-cards for the two decisions. All verbs/logic unchanged.
  const pill =
    'rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3.5 py-1.5 text-sm transition hover:border-foreground disabled:cursor-default disabled:opacity-50 dark:border-border dark:bg-card';
  const pillPrimary =
    'rounded-full bg-foreground px-4 py-1.5 text-sm text-card transition hover:opacity-90 disabled:cursor-default disabled:opacity-50';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="rd fixed inset-0 z-50 flex items-end justify-center bg-[#2d2822]/35 p-3 font-['DM_Sans'] text-foreground backdrop-blur-[2px] sm:items-center sm:p-6"
      onMouseDown={onClose}
    >
      <section
        onMouseDown={(e) => e.stopPropagation()}
        className="cr-modal flex max-h-[92vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[26px] border border-[#ded2c0] bg-popover shadow-[0_28px_80px_rgba(48,38,25,.25)] dark:border-border"
      >
        <div className="border-b border-[#e6dccd] px-5 pb-5 pt-5 sm:px-7 sm:pt-7 dark:border-border">
          <div className="flex items-start justify-between gap-5">
            <div>
              <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">{badge}</p>
              <h2 className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em]">{title}</h2>
            </div>
            <button
              onClick={onClose}
              aria-label="Close dialog"
              className="grid size-9 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              ✕
            </button>
          </div>
          {composed && card.data?.standfirst && (
            <p className="mt-4 max-w-[520px] text-[15px] leading-6 text-prose-foreground">{card.data.standfirst}</p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7">
        {!edgeKey ? (
          <>
            <p className="text-sm text-destructive">This conflict can&rsquo;t be opened — it has no backing decision item.</p>
            <div className="mt-4 flex justify-end">
              <button className={pill} onClick={onClose}>Close</button>
            </div>
          </>
        ) : card.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading the two decisions…</p>
        ) : card.isError || options.length === 0 ? (
          <>
            <p className="text-sm text-destructive">Couldn&rsquo;t load this conflict right now. Nothing was changed.</p>
            <div className="mt-4 flex justify-end">
              <button className={pill} onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">
              {mode === 'full' ? 'Choose a resolution' : 'The two positions'}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {options.map((opt) => (
                <OptionCard
                  key={opt.decisionId}
                  option={opt}
                  selectable={mode === 'full'}
                  selected={selectedDecisionId === opt.decisionId}
                  onSelect={() => {
                    setSelectedDecisionId(opt.decisionId);
                    setCustomText('');
                  }}
                  // Opens in a NEW TAB rather than routing the app away. Everything in flight here
                  // — the selected side, the custom resolution you typed, the teammate and ping
                  // note — is component state with no URL or storage backing, so a route change
                  // unmounted the dialog and destroyed it; back returned to the brief with the
                  // dialog closed and the text gone. The ↗ on this control now tells the truth.
                  onOpenSource={(convId) => window.open(`#/conversations/${convId}`, '_blank', 'noopener')}
                />
              ))}
            </div>

            {mode === 'full' && (
              <textarea
                className="mt-3 w-full rounded-xl border border-[#e1d7c8] bg-input-background p-3 text-sm leading-5 outline-none transition placeholder:text-muted-foreground focus:border-acc dark:border-border"
                rows={2}
                placeholder="…or write your own resolution"
                value={customText}
                onChange={(e) => {
                  setCustomText(e.target.value);
                  if (e.target.value) setSelectedDecisionId(null);
                }}
                disabled={busy}
              />
            )}

            {composed && card.data?.consequence && (
              <p className="mt-4 rounded-xl border border-[#d9cde9] bg-[#f3eef9] px-3 py-3 text-sm text-[#4a3d6b] dark:border-acc/30 dark:bg-acc-soft dark:text-foreground">
                {card.data.consequence}
              </p>
            )}

            {resolve.isError && (
              <p className="mt-3 text-sm text-destructive">Couldn&rsquo;t save that. Please try again — nothing was changed.</p>
            )}

            {mode === 'eval' ? (
              <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-[#e6dccd] pt-5 dark:border-border">
                <button className={pill} disabled={busy} onClick={() => resolve.mutate({ choice: 'not_conflict' })}>
                  These don&rsquo;t conflict
                </button>
                <button className={pillPrimary} disabled={busy} onClick={() => setMode('full')}>
                  It is a conflict →
                </button>
                <button className={pill} disabled={busy} onClick={onClose}>
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-[#e6dccd] pt-5 dark:border-border">
                  <button
                    className="mr-auto flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition hover:text-foreground"
                    disabled={busy}
                    onClick={() => resolve.mutate({ choice: 'snooze', snoozeMode: 'until_inflow' })}
                    title={snoozeLine(card.data?.snoozeCopy, 'until_inflow')}
                  >
                    Not now
                  </button>
                  <button
                    className={pill}
                    disabled={busy}
                    onClick={() => resolve.mutate({ choice: 'not_conflict' })}
                    title={verbLine(card.data?.verbCopy, 'not_conflict')}
                  >
                    These don&rsquo;t conflict
                  </button>
                  <button className={pill} disabled={busy} onClick={onClose}>
                    Cancel
                  </button>
                  <button className={pillPrimary} disabled={busy || !canResolve} onClick={submitResolve}>
                    Resolve →
                  </button>
                </div>

                {/* webapp-actions issue 5 — outbound actions on this conflict. Human-in-the-loop only: Ping
                    sends the sender's own note (Slack DM); Schedule/Follow-up open Google's / the mail app's
                    own compose prefilled — no silent write, no new scope. */}
                <div className="mt-4 border-t border-[#e6dccd] pt-4 dark:border-border">
                  <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">Move it forward</p>
                  {teammates.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <select
                        className="rounded-lg border border-[#e1d7c8] bg-input-background px-2.5 py-1.5 text-sm outline-none focus:border-acc dark:border-border"
                        value={teammateId}
                        onChange={(e) => setTeammateId(e.target.value)}
                        disabled={busy}
                        aria-label="Loop in a teammate"
                      >
                        <option value="">Loop in a teammate…</option>
                        {teammates.map((m) => (
                          <option key={m.user_id} value={m.user_id}>
                            {m.display_name}
                          </option>
                        ))}
                      </select>
                      <input
                        className="cr-ping-note min-w-0 flex-1 rounded-lg border border-[#e1d7c8] bg-input-background px-2.5 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-acc dark:border-border"
                        placeholder="Note (for Ping)"
                        value={pingNote}
                        onChange={(e) => setPingNote(e.target.value)}
                        disabled={busy}
                        aria-label="Ping note"
                      />
                      {/* Three DIFFERENT verbs, no longer one button wearing another's name (bug-557):
                          Route delegates silently, Blocked escalates with a DM, Ping nudges with a note.
                          Each carries the server's canonical one-liner verbatim as its tooltip. */}
                      <button
                        className={pill}
                        disabled={busy || !teammateId}
                        onClick={() => resolve.mutate({ choice: 'route', routeUserId: teammateId })}
                        title={verbLine(card.data?.verbCopy, 'route')}
                      >
                        Route
                      </button>
                      <button
                        className={pill}
                        disabled={busy || !teammateId}
                        onClick={() => resolve.mutate({ choice: 'blocked', blockedOnUserId: teammateId })}
                        title={verbLine(card.data?.verbCopy, 'blocked')}
                      >
                        Blocked on them
                      </button>
                      <button
                        className={pill}
                        disabled={busy || !teammateId || !pingNote.trim()}
                        onClick={() => resolve.mutate({ choice: 'ping', pingUserId: teammateId, note: pingNote.trim() })}
                        title={verbLine(card.data?.verbCopy, 'ping')}
                      >
                        Ping
                      </button>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={pill}
                      onClick={() =>
                        window.open(
                          calendarEventUrl(
                            `Discuss: ${title}`,
                            // Canonical live route — this lands in a calendar invite body, which is
                            // a permanent external record; it must not point at a retired address.
                            `Resolving a decision conflict surfaced in Terum — ${window.location.origin}/#/briefing`,
                          ),
                          '_blank',
                          'noreferrer',
                        )
                      }
                    >
                      Schedule sync
                    </button>
                    <button
                      type="button"
                      className={pill}
                      onClick={() =>
                        window.open(
                          followUpMailto(`Re: ${title}`, `Following up on the decision conflict "${title}" surfaced in Terum.`),
                          '_blank',
                          'noreferrer',
                        )
                      }
                    >
                      Follow up
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        </div>
      </section>
    </div>
  );
}
